// Canonical appearance options, shared by the photo-analysis prompt and the wizard swatches, so an AI
// answer always matches something a person can see selected. Pure and client-safe.

export const SKIN_TONE_OPTIONS = ['light', 'medium', 'olive', 'tan', 'brown', 'dark'] as const;
export const HAIR_COLOR_OPTIONS = ['blonde', 'brown', 'black', 'red', 'auburn', 'gray', 'white'] as const;
export const EYE_COLOR_OPTIONS = ['blue', 'green', 'brown', 'hazel', 'gray', 'amber', 'black'] as const;
export const HAIR_TEXTURE_OPTIONS = ['straight', 'wavy', 'curly', 'coily'] as const;

export type AppearanceField = 'skinTone' | 'hairColor' | 'eyeColor' | 'hairTexture';
export const APPEARANCE_FIELDS: AppearanceField[] = ['skinTone', 'hairColor', 'eyeColor', 'hairTexture'];
export type FieldConfidence = Record<AppearanceField, number>;

const OPTIONS: Record<AppearanceField, readonly string[]> = {
  skinTone: SKIN_TONE_OPTIONS,
  hairColor: HAIR_COLOR_OPTIONS,
  eyeColor: EYE_COLOR_OPTIONS,
  hairTexture: HAIR_TEXTURE_OPTIONS,
};

export const APPEARANCE_DEFAULTS: Record<AppearanceField, string> = {
  skinTone: 'medium', hairColor: 'brown', eyeColor: 'brown', hairTexture: 'straight',
};

// Words the model has been seen to answer with that are not options.
const SYNONYMS: Record<AppearanceField, Record<string, string>> = {
  skinTone: { fair: 'light', pale: 'light', beige: 'light', 'light brown': 'tan', 'dark brown': 'dark' },
  hairColor: {
    blond: 'blonde', 'dirty blonde': 'blonde', grey: 'gray', silver: 'gray', ginger: 'red',
    'strawberry blonde': 'red', 'dark brown': 'brown', 'light brown': 'brown', 'jet black': 'black',
  },
  eyeColor: { grey: 'gray', 'dark brown': 'brown', 'light brown': 'hazel', 'blue-green': 'green' },
  hairTexture: { kinky: 'coily', 'kinky-coily': 'coily', afro: 'coily', 'loose curls': 'wavy', 'tight curls': 'curly' },
};

export interface NormalizedTrait { value: string; known: boolean }

/** Map an AI answer onto the option list. An unknown answer falls back to the default and is flagged. */
export function normalizeTrait(field: AppearanceField, raw: unknown): NormalizedTrait {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (OPTIONS[field].includes(s)) return { value: s, known: true };
  const mapped = SYNONYMS[field][s];
  if (mapped) return { value: mapped, known: true };
  return { value: APPEARANCE_DEFAULTS[field], known: false };
}

function clamp01(n: unknown): number {
  const x = typeof n === 'number' ? n : Number.NaN;
  return Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0;
}

/** One person's appearance from the analysis JSON. An unknown value gets confidence 0. */
export function normalizeAppearance(raw: any): Record<AppearanceField, string> & { fieldConfidence: FieldConfidence } {
  const values = {} as Record<AppearanceField, string>;
  const fieldConfidence = {} as FieldConfidence;
  for (const f of APPEARANCE_FIELDS) {
    const t = normalizeTrait(f, raw?.[f]);
    values[f] = t.value;
    fieldConfidence[f] = t.known ? clamp01(raw?.fieldConfidence?.[f]) : 0;
  }
  return { ...values, fieldConfidence };
}

/** Value to prefill for one trait: '' (user must pick) when the AI could not read it (confidence exactly 0). */
export function prefillTrait(field: AppearanceField, aiValue: unknown, fieldConfidence?: Partial<FieldConfidence>): string {
  if (fieldConfidence?.[field] === 0) return '';
  const t = normalizeTrait(field, aiValue);
  return t.value;
}

/** Map legacy saved trait values ('silver', 'kinky') onto canonical ones. Unknown and empty values are left alone. */
export function canonicalizeCharacterTraits<T extends Partial<Record<AppearanceField, string>>>(char: T): T {
  const out: any = { ...char };
  for (const f of APPEARANCE_FIELDS) {
    const v = out[f];
    if (!v) continue;
    const t = normalizeTrait(f, v);
    if (t.known) out[f] = t.value;
  }
  return out;
}

/** Thresholds for skipping the appearance step. Tune here only. */
export const PREFILL_MIN_OVERALL = 0.6;
export const PREFILL_MIN_FIELD = 0.5;

export interface PrefilledCharacter {
  aiConfidence?: number;
  age?: string;
  gender?: string;
  aiFieldConfidence?: Partial<FieldConfidence>;
  skinTone?: string;
  hairColor?: string;
  eyeColor?: string;
  hairTexture?: string;
}

/** True when every character's AI appearance is sure enough to skip the appearance step. */
export function isAppearancePrefillConfident(chars: PrefilledCharacter[] | null | undefined): boolean {
  if (!chars || chars.length === 0) return false;
  return chars.every((c) =>
    (c.aiConfidence ?? 0) >= PREFILL_MIN_OVERALL &&
    !!c.age && !!c.gender &&
    APPEARANCE_FIELDS.every((f) => !!c[f] && (c.aiFieldConfidence?.[f] ?? 0) >= PREFILL_MIN_FIELD),
  );
}

/** True when every character has age, gender and all four looks filled in. */
export function hasAllRequiredTraits(chars: PrefilledCharacter[] | null | undefined): boolean {
  if (!chars || chars.length === 0) return false;
  return chars.every((c) => !!c.age && !!c.gender && APPEARANCE_FIELDS.every((f) => !!c[f]));
}

const quoted = (list: readonly string[]) => list.map((s) => `"${s}"`).join(', ');

/** The prompt lines for the four traits, generated from the same lists the swatches use. */
export function appearancePromptSpec(): string {
  return [
    `- hairColor: one of ${quoted(HAIR_COLOR_OPTIONS)}`,
    `- skinTone: one of ${quoted(SKIN_TONE_OPTIONS)}`,
    `- eyeColor: one of ${quoted(EYE_COLOR_OPTIONS)}`,
    `- hairTexture: one of ${quoted(HAIR_TEXTURE_OPTIONS)}`,
    '- fieldConfidence: object with a number 0-1 for each of "skinTone", "hairColor", "eyeColor", "hairTexture" ' +
      '(how sure you are of that one trait; below 0.5 when the face is small, blurry, in shadow, turned away, or the hair is covered)',
  ].join('\n');
}
